import { AllAssessmentStore } from './../../stores/all-assessments.store';
import { Component, ChangeDetectionStrategy, inject, Signal, OnInit } from '@angular/core';
import { Country } from '@shared/models/country.model';
import { AssessmentUser } from '../../models/assessment-user.model';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogActions, MatDialogContent, MatDialogTitle, MatDialogRef, MatDialogClose } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { ImprovedSelectComponent } from '@shared/components/improved-select/improved-select.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { AssessmentsDialogFilter } from '@features/assessments/models/assessment-filter.model';

type SelectConfig = {
  controlName: string;
  label: string;
  loadFn: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options: Signal<any[]>;
  multiple?: boolean;
  showChips?: boolean;
  displayKey?: string;
  loading?: Signal<boolean>;
};

@Component({
  selector: 'app-all-assessments-filter-dialog',
  imports: [
    MatButtonModule,
    MatIconModule,
    MatDialogClose,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    ReactiveFormsModule,
    ImprovedSelectComponent,
    MatDatepickerModule,
    MatFormFieldModule,
    MatCheckboxModule,
  ],
  templateUrl: './all-assessments-filter-dialog.component.html',
  styleUrls: ['./all-assessments-filter-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AllAssessmentsFilterDialogComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<AllAssessmentsFilterDialogComponent>);
  readonly fb = inject(NonNullableFormBuilder);
  readonly store = inject(AllAssessmentStore);

  countries = this.store.countries;

  industryNames = this.store.industryNames;

  // Initialize form with default empty values; actual stored filter patched on init
  filterForm = this.fb.group({
    country: [[] as Country[]],
    countryIntersection: [false],
    industry: [[] as string[]],
    company: [[] as string[]],
    project: [[] as string[]],
    dealId: [[] as string[]],
    underwritingSource: [[] as string[]],
    sensitivityLevel: [[] as string[]],
    mainDriver: [[] as string[]],
    createdById: [[] as AssessmentUser[]],
    businessUnit: [[] as string[]],
    legalEntity: [[] as string[]],
    // TODO: adjust formatting and timezones (if needed)
    dateFrom: [undefined as Date | undefined],
    dateTo: [undefined as Date | undefined],
  });

  configList: SelectConfig[] = [
    {
      controlName: 'country',
      label: 'Country',
      loadFn: () => this.store.loadCountries(),
      options: this.store.countries,
      multiple: true,
      displayKey: 'name',
      loading: this.store.countriesLoading,
    },
    {
      controlName: 'industry',
      label: 'Sector',
      loadFn: () => this.store.loadIndustryNames(),
      options: this.store.industryNames,
      multiple: true,
      loading: this.store.industryNamesLoading,
    },
    {
      controlName: 'company',
      label: 'Company',
      loadFn: () => this.store.loadCompanyNames(),
      options: this.store.companyNames,
      multiple: true,
      loading: this.store.companyNamesLoading,
    },
    {
      controlName: 'project',
      label: 'Project',
      loadFn: () => this.store.loadProjectNames(),
      options: this.store.projectNames,
      multiple: true,
      loading: this.store.projectNamesLoading,
    },
    {
      controlName: 'dealId',
      label: 'Deal ID',
      loadFn: () => this.store.loadDealIds(),
      options: this.store.dealIds,
      multiple: true,
      loading: this.store.dealIdsLoading,
    },
    {
      controlName: 'underwritingSource',
      label: 'Underwriting Source',
      loadFn: () => this.store.loadUnderwritingSources(),
      options: this.store.underwritingSources,
      multiple: true,
      loading: this.store.underwritingSourcesLoading,
    },
    {
      controlName: 'sensitivityLevel',
      label: 'Sensitivity Level',
      loadFn: () => this.store.loadSensitivityLevels(),
      options: this.store.sensitivityLevels,
      multiple: true,
      loading: this.store.sensitivityLevelsLoading,
    },
    {
      controlName: 'mainDriver',
      label: 'Main Driver',
      loadFn: () => this.store.loadMainDrivers(),
      options: this.store.mainDrivers,
      multiple: true,
      loading: this.store.mainDriversLoading,
    },
    {
      controlName: 'createdById',
      label: 'Created By',
      loadFn: () => this.store.loadAssessmentCreators(),
      options: this.store.assessmentCreators,
      multiple: true,
      displayKey: 'legalFullName',
      loading: this.store.assessmentCreatorsLoading,
    },
    {
      controlName: 'businessUnit',
      label: 'Business Unit',
      loadFn: () => this.store.loadBusinessUnits(),
      options: this.store.businessUnits,
      multiple: true,
      loading: this.store.businessUnitsLoading,
    },
    {
      controlName: 'legalEntity',
      label: 'Legal Entity',
      loadFn: () => this.store.loadLegalEntities(),
      options: this.store.legalEntities,
      multiple: true,
      loading: this.store.legalEntitiesLoading,
    },
  ];

  ngOnInit(): void {
    // Patch form with previously selected dialog filter values if any exist
    const currentFilter = this.store.selectedDialogFilter();
    if (currentFilter) {
      this.filterForm.patchValue({
        country: currentFilter.country ?? [],
        countryIntersection: currentFilter.countryIntersection ?? false,
        industry: currentFilter.industry ?? [],
        company: currentFilter.company ?? [],
        project: currentFilter.project ?? [],
        dealId: currentFilter.dealId ?? [],
        underwritingSource: currentFilter.underwritingSource ?? [],
        sensitivityLevel: currentFilter.sensitivityLevel ?? [],
        mainDriver: currentFilter.mainDriver ?? [],
        createdById: currentFilter.createdById ?? [],
        businessUnit: currentFilter.businessUnit ?? [],
        legalEntity: currentFilter.legalEntity ?? [],
        dateFrom: currentFilter.dateFrom,
        dateTo: currentFilter.dateTo,
      });
    }
  }

  onClear() {
    this.filterForm.reset();
  }

  onSubmit() {
    const filterData = {
      ...this.filterForm.value,
      dateFrom: this.filterForm.value.dateFrom || undefined,
      dateTo: this.filterForm.value.dateTo || undefined,
    } as AssessmentsDialogFilter;

    this.store.updateSelectedFilter(filterData);
    this.store.submitFilter();
    this.dialogRef.close();
  }
}
